import React, { useEffect, useLayoutEffect, useMemo } from 'react';

export type OrdenAngelesFieldKey =
  | 'patientName'
  | 'age'
  | 'ordenTexto'
  | 'footerDate'
  | 'doctorName';

export interface FieldPosition {
  top: number;
  left: number;
  width?: number;
}

export interface OrdenAngelesCanvasProps {
  patientName: string;
  ageYears: string;
  ordenTexto: string;
  footerDate: string;
  doctorName: string;
  className?: string;
  editMode?: boolean;
  selectedField?: OrdenAngelesFieldKey | null;
  onSelectField?: (field: OrdenAngelesFieldKey) => void;
  fieldPositions?: Partial<Record<OrdenAngelesFieldKey, FieldPosition>>;
  onFieldBoundsChange?: (field: OrdenAngelesFieldKey, size: { width: number; height: number }) => void;
}

export const DEFAULT_ORDEN_ANGELES_POSITIONS: Record<OrdenAngelesFieldKey, FieldPosition> = {
  patientName: { top: 1270, left: 250, width: 1300 },
  age: { top: 1350, left: 250, width: 1300 },
  ordenTexto: { top: 1590, left: 250 },
  footerDate: { top: 2130, left: 250 },
  doctorName: { top: 2130, left: 1100 },
};

export const ORDEN_ANGELES_FIELD_KEYS = Object.keys(DEFAULT_ORDEN_ANGELES_POSITIONS) as OrdenAngelesFieldKey[];

const OrdenAngelesCanvas: React.FC<OrdenAngelesCanvasProps> = ({
  patientName,
  ageYears,
  ordenTexto,
  footerDate,
  doctorName,
  className = '',
  editMode = false,
  selectedField,
  onSelectField,
  fieldPositions,
  onFieldBoundsChange,
}) => {
  const mergedPositions = useMemo(() => {
    return ORDEN_ANGELES_FIELD_KEYS.reduce<Record<OrdenAngelesFieldKey, FieldPosition>>((acc, key) => {
      const base = DEFAULT_ORDEN_ANGELES_POSITIONS[key];
      const override = fieldPositions?.[key];
      acc[key] = override ? { ...base, ...override } : { ...base };
      return acc;
    }, {} as Record<OrdenAngelesFieldKey, FieldPosition>);
  }, [fieldPositions]);

  const handleSelect = (field: OrdenAngelesFieldKey) => {
    if (editMode) {
      onSelectField?.(field);
    }
  };

  const refs = useMemo(() => {
    return ORDEN_ANGELES_FIELD_KEYS.reduce<Record<OrdenAngelesFieldKey, React.RefObject<HTMLDivElement>>>((acc, key) => {
      acc[key] = React.createRef<HTMLDivElement>();
      return acc;
    }, {} as Record<OrdenAngelesFieldKey, React.RefObject<HTMLDivElement>>);
  }, []);

  useLayoutEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    (Object.keys(refs) as OrdenAngelesFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (el) {
        const rect = el.getBoundingClientRect();
        onFieldBoundsChange(key, { width: rect.width, height: rect.height });
      }
    });
  }, [patientName, ageYears, ordenTexto, footerDate, doctorName, editMode, refs, onFieldBoundsChange]);

  useEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    const observers: ResizeObserver[] = [];

    (Object.keys(refs) as OrdenAngelesFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (!el) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          onFieldBoundsChange(key, { width, height });
        }
      });

      observer.observe(el);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [refs, onFieldBoundsChange]);

  const classSuffix: Record<OrdenAngelesFieldKey, string> = {
    patientName: 'patient-name',
    age: 'age',
    ordenTexto: 'orden-texto',
    footerDate: 'footer-date',
    doctorName: 'doctor-name',
  };

  const styleFor = (field: OrdenAngelesFieldKey): React.CSSProperties => {
    const pos = mergedPositions[field];
    const base: React.CSSProperties = {
      top: `${pos.top / 100}cm`,
      left: `${pos.left / 100}cm`,
    };
    if (pos.width != null) {
      base.width = `${pos.width / 100}cm`;
    }
    if (editMode) {
      base.cursor = 'move';
    }
    return base;
  };

  const focusableProps = (field: OrdenAngelesFieldKey) => {
    const baseClass = `recipe-field orden-angeles-field-${classSuffix[field]}`;

    if (editMode) {
      return {
        tabIndex: 0,
        onClick: () => handleSelect(field),
        onFocus: () => handleSelect(field),
        className: `${baseClass} ${selectedField === field ? 'recipe-field-selected' : ''}`.trim(),
      };
    }

    return {
      className: baseClass,
    };
  };

  const ageText = `${ageYears} AÑOS`;

  return (
    <div
      className={`recipe-print-page orden-angeles ${editMode ? 'recipe-print-page--edit' : ''} ${className}`.trim()}
    >
      <div ref={refs.patientName} style={styleFor('patientName')} {...focusableProps('patientName')}>
        {patientName}
      </div>
      <div ref={refs.age} style={styleFor('age')} {...focusableProps('age')}>
        {ageText}
      </div>
      <div ref={refs.ordenTexto} style={styleFor('ordenTexto')} {...focusableProps('ordenTexto')}>
        <span className="whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', tabSize: 38 }}>{ordenTexto}</span>
      </div>
      <div ref={refs.footerDate} style={styleFor('footerDate')} {...focusableProps('footerDate')}>
        {footerDate}
      </div>
      <div ref={refs.doctorName} style={{ ...styleFor('doctorName'), fontSize: '11pt' }} {...focusableProps('doctorName')}>
        {doctorName}
      </div>
    </div>
  );
};

export default OrdenAngelesCanvas;
